import axios from 'axios';
import debounce from 'lodash.debounce';
import sortBy from 'lodash.sortby';
import { useCallback, useEffect, useState } from 'react';

const {
  REACT_APP_DITCH_SEARCH_URL: ditchSearchUrl = '',
  REACT_APP_MAPBOX_ACCESS_TOKEN: mapboxAccessToken = '',
  REACT_APP_MAPBOX_GEOCODE_URL: mapboxGeocodeUrl = '',
  REACT_APP_SEARCH_BBOX: bbox = '',
} = process.env;

export interface SearchResult {
  primaryText: string;
  secondaryText?: string;
  locationType: string;
  /** Location coordinates in the form [lng, lat] */
  coordinates: [number, number];
}

interface UseSearchReturns {
  data: SearchResult[];
  error: any[];
}

interface ResultsState {
  ditchData: SearchResult[];
  data: SearchResult[];
  error: any[];
}

const initialResultsState = { ditchData: [], data: [], error: [] };

export const useSearch = (query: string): UseSearchReturns => {
  const [previousQuery, setPreviousQuery] = useState<string>('');
  const [resultsState, setResultsState] = useState<ResultsState>(initialResultsState);

  /**
   * Perform search and persist to `resultsState`.
   * Callback is debounced to prevent multiple simulatneous requests as query is changing.
   * Ditches are loaded once to state if not available. After, they are only filtered locally.
   */
  const doSearch = useCallback(
    debounce<(q: string) => Promise<void>>(async (q: string) => {
      const data: SearchResult[] = [];
      const error: any[] = [];

      let ditches = resultsState.ditchData;

      if (!ditches.length) {
        try {
          ditches = await loadDitches({ url: ditchSearchUrl });
        } catch (err) {
          error.push(err);
        }
      }

      try {
        const geocodeResults = await geocode(q, {
          url: mapboxGeocodeUrl,
          token: mapboxAccessToken,
          bbox: JSON.parse(bbox),
        });
        data.push(...geocodeResults);
      } catch (err) {
        error.push(err);
      }

      data.push(...searchDitches(ditches, q));

      setResultsState({ ditchData: ditches, data, error });
    }, 400),
    [resultsState, setResultsState]
  );

  useEffect(() => {
    if (!query || !query.length || query === previousQuery) {
      // TODO. Potentially reset results, but leaving last results in list doesn't feel bad either
      return;
    }
    setPreviousQuery(query);
    doSearch(query);
  }, [previousQuery, setPreviousQuery, query, doSearch]);

  return {
    data: resultsState.data,
    error: resultsState.error,
  };
};

/**
 * @param query Text to geocode
 * @param options Configuration options
 * @param options.url Mapbox geocode service URL like https://api.mapbox.com/geocoding/v5/mapbox.places
 * @param options.token Mapbox access token
 * @param options.bbox Bounding box to limit search results in format [xmin, ymin, xmax, ymax]
 */
async function geocode(
  query: string,
  { url, token, bbox }: { url: string; token: string; bbox: [number, number, number, number] }
): Promise<SearchResult[]> {
  interface GeocodeFeatureData {
    place_name: string;
    place_type: string;
    center: [number, number];
  }

  const response = await axios.get(`${url}/${query}.json`, {
    params: {
      access_token: token,
      country: 'US',
      bbox: bbox,
      proximity: [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2],
      types: 'address',
      limit: 5,
    },
  });

  return response.data.features.map(({ place_name, place_type, center }: GeocodeFeatureData) => {
    const splitPlace = place_name.split(',');
    return {
      primaryText: splitPlace[0],
      secondaryText: `${splitPlace[1]}, ${splitPlace[2]}`,
      locationType: place_type[0],
      coordinates: center,
    };
  });
}

/**
 * Load all ditches from the source. Used to load all data locally and later filter at the client.
 * @param options Configuration options
 * @param options.url URL to fetch ditch data
 */
export async function loadDitches({ url }: { url: string }): Promise<SearchResult[]> {
  interface DitchFeatureData {
    number: string;
    name: string;
    coordinates: [number, number];
  }

  const response = await axios.get(url);

  return response.data.map(({ number, name, coordinates }: DitchFeatureData) => ({
    primaryText: `Ditch ${number}`,
    secondaryText: name || undefined,
    locationType: 'ditch',
    coordinates: coordinates,
  }));
}

/**
 * Find all ditches from `features` with a primaryText (number) or secondaryText (name) matching the `query`
 * @param features Ditch features to search
 * @param query Search text
 */
function searchDitches(features: SearchResult[], query: string): SearchResult[] {
  const queryLower = query.toLowerCase();
  const matched = features.filter(
    ({ primaryText, secondaryText = '' }) =>
      primaryText.toLowerCase().includes(queryLower) ||
      secondaryText.toLowerCase().includes(queryLower)
  );
  const sorted = sortBy(matched, ['primaryText', 'secondaryText']);
  // 25 seems like a reasonable number to capture all relevant for a search like 100 (100-A, 100-B, etc..)
  // but not return an insane amount for a search like 1
  return sorted.slice(0, 25);
}

export default useSearch;
